// step 2 of the sponsor pledge flow

import React from 'react'
import { Trans } from 'react-i18next'

import Icon from '../../../../components/icon'
import EzTooltip from '../../../../components/tooltip'
import { formatAmount } from '../../../../lib/numbers'

function UnderlinedTooltip({ id, tip, className, children }) {
  return (
    <EzTooltip
      id={id}
      type="primary"
      placement="top"
      outerContent={<span className={className}>{children}</span>}
      innerContent={tip}
    />
  )
}

export default function Step2Allocation({
  styles,
  t,
  commitmentNum,
  sliderPct,
  onSlider,
  allocWarn,
  vpfAmount,
  mpfAmount,
  onVpfInput,
  onMpfInput,
  vpfInputDraft,
  mpfInputDraft,
  setIsEditingVpf,
  setIsEditingMpf,
  setVpfInputDraft,
  setMpfInputDraft,
  volunteerCount,
  estimatedVolunteerHours,
  vpfVolunteerShare,
  vpfOrgShare,
  setStep,
}) {
  const tooltipTriggers = {
    eventMultiplier: (
      <UnderlinedTooltip
        id="sponsor-pledge-step2-event-multiplier"
        tip={t('sponsorPledgeFlow.tier.tooltips.eventMultiplier')}
        className={styles.tooltipTrigger}
      />
    ),
    vpf: (
      <UnderlinedTooltip
        id="sponsor-pledge-step2-vpf"
        tip={t('sponsorPledgeFlow.tier.tooltips.vpf')}
        className={styles.tooltipTrigger}
      />
    ),
    mpf: (
      <UnderlinedTooltip
        id="sponsor-pledge-step2-mpf"
        tip={t('sponsorPledgeFlow.tier.tooltips.mpf')}
        className={styles.tooltipTrigger}
      />
    ),
  }

  return (
    <>
      <h1 className={styles.stepHeading}>
        {t('sponsorPledgeFlow.step2.heading')}
      </h1>
      <p className={styles.stepSubheading}>
        {t('sponsorPledgeFlow.step2.subheading')}
      </p>

      <div className={styles.allocCard}>
        <div className={styles.allocCardHeader}>
          <div>
            <div className={styles.allocCardSublabel}>
              {t('sponsorPledgeFlow.step2.cardPledgeLabel')}
            </div>
            <div className={styles.allocCardPledge}>
              {formatAmount(commitmentNum, true)}
            </div>
          </div>
          <div>
            <div
              className={styles.allocCardSublabel}
              style={{ textAlign: 'right' }}
            >
              {t('sponsorPledgeFlow.step2.cardImpactLabel')}
            </div>
            <div className={styles.allocImpact}>
              {formatAmount(commitmentNum, true)}
            </div>
            <div className={styles.allocImpactSub}>
              {t('sponsorPledgeFlow.step2.cardImpactSub')}
            </div>
          </div>
        </div>

        <div className={styles.allocSplitLabel}>
          <Trans
            i18nKey="sponsorPledgeFlow.step2.splitLabel"
            components={tooltipTriggers}
          />
        </div>
        <div className={styles.allocSliderWrap}>
          <div className={styles.allocBarTrack}>
            <div
              className={styles.allocBarVpf}
              style={{ width: `${sliderPct}%` }}
            />
            <div
              className={styles.allocBarMpf}
              style={{ width: `${100 - sliderPct}%` }}
            />
          </div>
          <input
            type="range"
            className={styles.crSlider}
            min={0}
            max={100}
            value={sliderPct}
            onChange={onSlider}
          />
        </div>
        <div className={styles.splitLabels}>
          <span style={{ color: 'var(--purple)', fontWeight: 600 }}>
            <Trans
              i18nKey="sponsorPledgeFlow.step2.vpfPct"
              values={{ pct: sliderPct }}
              components={tooltipTriggers}
            />
          </span>
          <span style={{ color: 'var(--pink)', fontWeight: 600 }}>
            <Trans
              i18nKey="sponsorPledgeFlow.step2.mpfPct"
              values={{ pct: 100 - sliderPct }}
              components={tooltipTriggers}
            />
          </span>
        </div>

        <div className={styles.allocCols}>
          <div className={styles.allocCol}>
            <div className={styles.allocColHead}>
              <div className={styles.allocBadgeRow}>
                <div
                  className={`${styles.allocColBadge} ${styles.allocColBadgeVpf}`}
                >
                  VPF
                </div>
                <div className={styles.allocInfoWrap}>
                  <button
                    type="button"
                    className={styles.allocInfoBtn}
                    aria-label={t('sponsorPledgeFlow.tier.tooltips.vpf')}
                  >
                    <Icon name="info-circle" />
                  </button>
                  <div className={styles.allocInfoTooltip}>
                    {t('sponsorPledgeFlow.tier.tooltips.vpf')}
                  </div>
                </div>
              </div>
              <div className={styles.allocColAmountWrap}>
                <span style={{ fontWeight: 600, color: 'var(--light)' }}>
                  $
                </span>
                <input
                  className={styles.allocColAmount}
                  type="text"
                  inputMode="decimal"
                  value={vpfInputDraft}
                  onChange={onVpfInput}
                  onFocus={() => setIsEditingVpf(true)}
                  onBlur={() => {
                    setIsEditingVpf(false)
                    setVpfInputDraft(vpfAmount.toFixed(2))
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--light)' }}>
                {t('sponsorPledgeFlow.step2.volHourLine', {
                  vol: volunteerCount,
                  hrs: estimatedVolunteerHours ?? '—',
                })}
              </div>
            </div>
            <div className={styles.allocColBody}>
              <div className={styles.allocColStat}>
                {t('sponsorPledgeFlow.step2.vpfVolShare', {
                  amount: formatAmount(vpfVolunteerShare, true),
                })}
              </div>
              <div className={styles.allocColStat} style={{ marginTop: 8 }}>
                {t('sponsorPledgeFlow.step2.vpfOrgShare', {
                  amount: formatAmount(vpfOrgShare, true),
                })}
              </div>
            </div>
          </div>
          <div className={styles.allocCol}>
            <div className={styles.allocColHead}>
              <div className={styles.allocBadgeRow}>
                <div
                  className={`${styles.allocColBadge} ${styles.allocColBadgeMpf}`}
                >
                  MPF
                </div>
                <div className={styles.allocInfoWrap}>
                  <button
                    type="button"
                    className={styles.allocInfoBtn}
                    aria-label={t('sponsorPledgeFlow.tier.tooltips.mpf')}
                  >
                    <Icon name="info-circle" />
                  </button>
                  <div className={styles.allocInfoTooltip}>
                    {t('sponsorPledgeFlow.tier.tooltips.mpf')}
                  </div>
                </div>
              </div>
              <div className={styles.allocColAmountWrap}>
                <span style={{ fontWeight: 600, color: 'var(--light)' }}>
                  $
                </span>
                <input
                  className={styles.allocColAmount}
                  type="text"
                  inputMode="decimal"
                  value={mpfInputDraft}
                  onChange={onMpfInput}
                  onFocus={() => setIsEditingMpf(true)}
                  onBlur={() => {
                    setIsEditingMpf(false)
                    setMpfInputDraft(mpfAmount.toFixed(2))
                  }}
                />
              </div>
              <div style={{ fontSize: 11, color: 'var(--light)' }}>
                {t('sponsorPledgeFlow.step2.mpfMaxMatch')}
              </div>
            </div>
            <div className={styles.allocColBody}>
              <div className={styles.allocColStat}>
                {t('sponsorPledgeFlow.step2.mpfDonors', {
                  amount: formatAmount(mpfAmount, true),
                })}
              </div>
              <div className={styles.allocColStat} style={{ marginTop: 8 }}>
                {t('sponsorPledgeFlow.step2.mpfMatch', {
                  amount: formatAmount(mpfAmount, true),
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => setStep(1)}
        >
          ‹ {t('buttons.back')}
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={allocWarn || !commitmentNum}
          onClick={() => setStep(3)}
        >
          {t('buttons.continue')}
          <Icon name="chevron-right" />
        </button>
      </div>
    </>
  )
}
